import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LucideIcon, ChevronRight, Star } from "lucide-react";

interface InteractiveCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  category: string;
  status?: "active" | "completed" | "pending";
  progress?: number;
  onClick?: () => void;
  delay?: number;
  featured?: boolean;
}

export const InteractiveCard = ({
  title,
  description,
  icon: Icon,
  category,
  status = "active",
  progress,
  onClick,
  delay = 0,
  featured = false
}: InteractiveCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const getStatusColor = () => {
    switch (status) {
      case "completed":
        return "bg-green-500 text-white";
      case "pending":
        return "bg-orange-500 text-white";
      default:
        return "bg-[#c4ab83] text-white";
    }
  };

  const cardClasses = featured 
    ? "bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group overflow-hidden relative" 
    : "bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group overflow-hidden relative";

  return (
    <Card 
      className={cardClasses}
      style={{ animationDelay: `${delay}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {featured && (
        <div className="absolute top-3 right-3 z-10">
          <Star className="w-4 h-4 text-[#c4ab83] animate-pulse-glow" fill="currentColor" />
        </div>
      )}
      
      {/* Header com gradiente dourado */}
      <div className="relative bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg p-4">
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-xl transition-all duration-300 ${
            featured 
              ? "bg-gradient-to-br from-[#c4ab83] to-[#b49a73] shadow-md" 
              : "bg-gradient-to-br from-[#c4ab83]/20 to-[#b49a73]/20 group-hover:from-[#c4ab83]/30 group-hover:to-[#b49a73]/30"
          }`}>
            <Icon className={`w-6 h-6 transition-all duration-300 ${
              featured ? "text-white" : "text-[#c4ab83]"
            } ${isHovered ? "scale-110" : ""}`} />
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs bg-[#c4ab83]/20 border-[#c4ab83] text-[#c4ab83] font-medium">
              {category}
            </Badge>
            <Badge className={`text-xs ${getStatusColor()}`}>
              {status === 'completed' ? 'Concluído' : status === 'pending' ? 'Pendente' : 'Ativo'}
            </Badge>
          </div>
        </div>
      </div>
      
      <CardContent className="p-4 space-y-3 bg-gray-50/30">
        <div className="space-y-3">
          <h3 className={`font-semibold text-lg transition-colors duration-300 text-black font-bold ${
            featured 
              ? "group-hover:text-[#c4ab83]" 
              : "group-hover:text-[#c4ab83]"
          }`}>
            {title}
          </h3>
          
          <p className="text-sm text-gray-600 leading-relaxed">
            {description}
          </p>

          {progress !== undefined && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Progresso</span>
                <span className="font-medium text-[#c4ab83]">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#c4ab83] to-[#b49a73] rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="hover:bg-[#c4ab83]/20 hover:text-[#c4ab83] hover:border-[#c4ab83] transition-colors"
            >
              Acessar
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};